/**
 * IndexedDB utility for storing timesheets offline
 */

const DB_NAME = '3pm-base-offline';
const DB_VERSION = 2; // Incremented to force schema refresh
const STORE_TIMESHEETS = 'timesheets';
const STORE_QUEUE = 'queue';

interface QueuedRequest {
  id?: number;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  timestamp: number;
  retryCount: number;
}

interface OfflineTimesheet {
  id: string;
  data: any;
  status: 'draft' | 'submitted';
  timestamp: number;
  synced: boolean;
}

class OfflineStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Delete old stores if they exist (for clean migration)
        if (db.objectStoreNames.contains(STORE_TIMESHEETS)) {
          db.deleteObjectStore(STORE_TIMESHEETS);
        }
        if (db.objectStoreNames.contains(STORE_QUEUE)) {
          db.deleteObjectStore(STORE_QUEUE);
        }

        // Create fresh stores
        const timesheetStore = db.createObjectStore(STORE_TIMESHEETS, { keyPath: 'id' });
        timesheetStore.createIndex('synced', 'synced', { unique: false });
        timesheetStore.createIndex('timestamp', 'timestamp', { unique: false });

        db.createObjectStore(STORE_QUEUE, { keyPath: 'id', autoIncrement: true });
      };
    });
  }

  // Timesheet operations
  async saveTimesheet(timesheetData: any, status: 'draft' | 'submitted'): Promise<string> {
    await this.ensureDB();
    const id = `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const offlineTimesheet: OfflineTimesheet = {
      id,
      data: timesheetData,
      status,
      timestamp: Date.now(),
      synced: false
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_TIMESHEETS], 'readwrite');
      const store = transaction.objectStore(STORE_TIMESHEETS);
      const request = store.add(offlineTimesheet);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(id);
    });
  }

  async getTimesheet(id: string): Promise<OfflineTimesheet | null> {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_TIMESHEETS], 'readonly');
      const store = transaction.objectStore(STORE_TIMESHEETS);
      const request = store.get(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
    });
  }

  async getAllUnsyncedTimesheets(): Promise<OfflineTimesheet[]> {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([STORE_TIMESHEETS], 'readonly');
        const store = transaction.objectStore(STORE_TIMESHEETS);
        
        // Get all timesheets and filter manually
        // Boolean key ranges are not reliably supported across all browsers
        // Using store.getAll() directly, not index.getAll()
        const request = store.getAll();

        request.onerror = () => {
          console.error('[OfflineStorage] Error getting all timesheets:', request.error);
          reject(request.error);
        };
        
        request.onsuccess = () => {
          try {
            const allTimesheets = request.result || [];
            const unsynced = allTimesheets.filter((ts: OfflineTimesheet) => ts.synced === false);
            resolve(unsynced);
          } catch (error) {
            console.error('[OfflineStorage] Error filtering timesheets:', error);
            reject(error);
          }
        };
      } catch (error) {
        console.error('[OfflineStorage] Error in getAllUnsyncedTimesheets:', error);
        reject(error);
      }
    });
  }

  async markTimesheetSynced(id: string): Promise<void> {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_TIMESHEETS], 'readwrite');
      const store = transaction.objectStore(STORE_TIMESHEETS);
      const getRequest = store.get(id);

      getRequest.onerror = () => reject(getRequest.error);
      getRequest.onsuccess = () => {
        const timesheet = getRequest.result;
        if (timesheet) {
          timesheet.synced = true;
          const putRequest = store.put(timesheet);
          putRequest.onerror = () => reject(putRequest.error);
          putRequest.onsuccess = () => resolve();
        } else {
          resolve();
        }
      };
    });
  }

  async deleteTimesheet(id: string): Promise<void> {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_TIMESHEETS], 'readwrite');
      const store = transaction.objectStore(STORE_TIMESHEETS);
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  // Queue operations
  async queueRequest(request: Omit<QueuedRequest, 'id' | 'timestamp' | 'retryCount'>): Promise<number> {
    await this.ensureDB();
    const queuedRequest: QueuedRequest = {
      ...request,
      timestamp: Date.now(),
      retryCount: 0
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORE_QUEUE);
      const addRequest = store.add(queuedRequest);

      addRequest.onerror = () => reject(addRequest.error);
      addRequest.onsuccess = () => resolve(addRequest.result as number);
    });
  }

  async getQueuedRequests(): Promise<QueuedRequest[]> {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_QUEUE], 'readonly');
      const store = transaction.objectStore(STORE_QUEUE);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || []);
    });
  }

  async removeQueuedRequest(id: number): Promise<void> {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORE_QUEUE);
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  private async ensureDB(): Promise<void> {
    if (!this.db) {
      await this.init();
    }
  }
}

export const offlineStorage = new OfflineStorage();

